import { db } from '../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const DEFAULT_BRAND_ID = 'lumirae';
let sessionId = null;
let sessionStartTime = Date.now();

export const EVENTS = {
  PAGE_CREATED: 'page_created',
  PAGE_VIEW: 'page_view',
  PAGE_LOAD_START: 'page_load_start',
  PAGE_LOAD_SUCCESS: 'page_load_success',
  PAGE_LOAD_FAILED: 'page_load_failed',
  PAGE_EXIT: 'page_exit',
  TIME_SPENT: 'time_spent',
  EDIT_OPENED: 'edit_opened',
  PAGE_SAVE_START: 'page_save_start',
  PAGE_SAVED: 'page_saved',
  PAGE_SAVE_SUCCESS: 'page_save_success',
  PAGE_SAVE_FAILED: 'page_save_failed',
  MODULE_ADDED: 'module_added',
  MODULE_DELETED: 'module_deleted',
  MODULE_VIEW: 'module_view',
  MODULE_CLICK: 'module_click',
  MODULE_INTERACT: 'module_interact',
  MODULE_ERROR: 'module_error',
  MEDIA_UPLOADED: 'media_uploaded',
  UPLOAD_START: 'upload_start',
  UPLOAD_SUCCESS: 'upload_success',
  UPLOAD_FAILED: 'upload_failed',
  AUDIO_PLAY: 'audio_play',
  VIDEO_PLAY: 'video_play',
  MEDIA_PLAY: 'media_play',
  MEDIA_PAUSE: 'media_pause',
  MEDIA_COMPLETE: 'media_complete',
  MEDIA_ERROR: 'media_error',
  TEMPLATE_SELECTED: 'template_selected',
  TEMPLATE_APPLIED: 'template_applied',
  TEMPLATE_REMOVED: 'template_removed',
  THEME_CHANGED: 'theme_changed',
  GIFT_SHARED: 'gift_shared',
  SHARE_CLICK: 'share_click',
  COPY_LINK: 'copy_link',
  QR_DOWNLOAD: 'qr_download',
  STORY_IMAGE_SAVED: 'story_image_saved',
  UNBOXING_START: 'unboxing_start',
  UNBOXING_COMPLETE: 'unboxing_complete',
  ADMIN_LOGIN: 'admin_login',
  APP_ERROR: 'app_error',
  FIREBASE_ERROR: 'firebase_error',
};

const getSessionId = () => {
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  return sessionId;
};

const getDeviceInfo = () => {
  if (typeof window === 'undefined') return {};
  return {
    isMobile: /Mobi|Android/i.test(navigator.userAgent),
    browser: navigator.userAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i)?.[1] || 'Unknown',
    platform: navigator.platform,
    language: navigator.language,
    userAgent: navigator.userAgent,
  };
};

const sanitizeData = (data) => {
  if (!data) return {};
  const sensitiveFields = ['ownerName', 'ritualText', 'title', 'body', 'content', 'text', 'message', 'noteText', 'description', 'caption', 'email', 'phone', 'address', 'password'];
  const sanitized = { ...data };
  
  Object.keys(sanitized).forEach(key => {
    if (sensitiveFields.includes(key)) {
      delete sanitized[key];
      return;
    }
    if (sanitized[key] === undefined || sanitized[key] === null) {
      delete sanitized[key];
    }
  });
  
  return sanitized;
};

export const logEvent = async (pageId, eventName, metadata = {}) => {
  if (!eventName) return;
  try {
    const deviceInfo = getDeviceInfo();
    const sessionId = getSessionId();
    const { moduleType = null, moduleId = null, userType = 'viewer', action = null, ...restData } = metadata;

    const eventDoc = {
      event: eventName,
      pageId: pageId || 'unknown',
      sessionId,
      userType,
      moduleType,
      moduleId,
      data: sanitizeData({ action, ...restData }),
      device: deviceInfo,
      url: typeof window !== 'undefined' ? window.location.href : '',
      referrer: typeof document !== 'undefined' ? document.referrer : '',
      createdAt: serverTimestamp(),
      brandId: DEFAULT_BRAND_ID,
    };

    Object.keys(eventDoc).forEach(key => {
      if (eventDoc[key] === undefined || eventDoc[key] === null) delete eventDoc[key];
    });

    await addDoc(collection(db, 'analytics'), eventDoc);
  } catch (err) {
    console.warn('Analytics logEvent failed:', err);
  }
};

export const trackPageLoadStart = (pageId) => logEvent(pageId, EVENTS.PAGE_LOAD_START, { action: 'app_init' });
export const trackPageLoadSuccess = (pageId, moduleCount = 0) => logEvent(pageId, EVENTS.PAGE_LOAD_SUCCESS, { moduleCount });
export const trackPageLoadFailed = (pageId, errorMessage) => logEvent(pageId, EVENTS.PAGE_LOAD_FAILED, { errorMessage, componentName: 'App' });
export const trackPageView = (pageId, source = 'page_open') => logEvent(pageId, EVENTS.PAGE_VIEW, { source });
export const trackPageExit = (pageId) => logEvent(pageId, EVENTS.PAGE_EXIT);
export const trackTimeSpent = (pageId, durationSeconds = 0) => {
  const timeSpent = durationSeconds || Math.round((Date.now() - sessionStartTime) / 1000);
  if (timeSpent > 1) logEvent(pageId, EVENTS.TIME_SPENT, { durationSeconds: timeSpent });
};
export const trackEditOpened = (pageId) => logEvent(pageId, EVENTS.EDIT_OPENED, { userType: 'admin' });
export const trackPageSaveStart = (pageId, moduleCount) => logEvent(pageId, EVENTS.PAGE_SAVE_START, { moduleCount });
export const trackPageSaveSuccess = (pageId) => logEvent(pageId, EVENTS.PAGE_SAVE_SUCCESS);
export const trackPageSaveFailed = (pageId, error) => logEvent(pageId, EVENTS.PAGE_SAVE_FAILED, { error });
export const trackModuleView = (pageId, moduleType, moduleId, position = 0) => logEvent(pageId, EVENTS.MODULE_VIEW, { moduleType, moduleId, position });
export const trackModuleClick = (pageId, moduleType, moduleId) => logEvent(pageId, EVENTS.MODULE_CLICK, { moduleType, moduleId });
export const trackModuleInteract = (pageId, moduleType, moduleId, action, metadata = {}) => logEvent(pageId, EVENTS.MODULE_INTERACT, { moduleType, moduleId, action, ...metadata });
export const trackClick = (pageId, action, metadata = {}) => logEvent(pageId, EVENTS.MODULE_CLICK, { action, ...metadata });
export const trackMediaPlay = (pageId, moduleType, moduleId, metadata = {}) => logEvent(pageId, EVENTS.MEDIA_PLAY, { moduleType, moduleId, ...metadata });
export const trackMediaPause = (pageId, moduleType, moduleId, metadata = {}) => logEvent(pageId, EVENTS.MEDIA_PAUSE, { moduleType, moduleId, ...metadata });
export const trackMediaComplete = (pageId, moduleType, moduleId, metadata = {}) => logEvent(pageId, EVENTS.MEDIA_COMPLETE, { moduleType, moduleId, ...metadata });
export const trackMediaError = (pageId, moduleType, moduleId, error, metadata = {}) => logEvent(pageId, EVENTS.MEDIA_ERROR, { moduleType, moduleId, error, ...metadata });
export const trackTemplateSelected = (pageId, templateId) => logEvent(pageId, EVENTS.TEMPLATE_SELECTED, { templateId });
export const trackThemeChanged = (pageId, themeId) => logEvent(pageId, EVENTS.THEME_CHANGED, { themeId });
export const trackStoryImageSaved = (pageId, moduleType, moduleId) => logEvent(pageId, EVENTS.STORY_IMAGE_SAVED, { moduleType, moduleId });
export const trackAdminLogin = (pageId, status = 'success') => logEvent(pageId, EVENTS.ADMIN_LOGIN, { userType: 'admin', action: status });
export const trackAppError = (pageId, error, componentName) => logEvent(pageId, EVENTS.APP_ERROR, { error, componentName });

export const getTemplateStats = async () => ({});
export const getThemeStats = async () => ({});
export const getModuleStats = async () => ({});
export const getShareStats = async () => ({ total: 0, byPlatform: {} });
export const getEngagementStats = async (pageId) => ({ totalEvents: 0, uniqueEvents: 0 });
export const trackEvent = async (eventName, { pageId, moduleType, moduleId, userType, action, data } = {}) => {
  return logEvent(pageId, eventName, { moduleType, moduleId, userType, action, ...data });
};
